import { buildOrganizedTourProfileViewModel } from "./buildOrganizedTourProfileViewModel";
import {
  extractOrganizedTourFlightLegs,
  formatOrganizedTourFlightDateTime,
} from "./organizedTourProfileFlights";
import { resolveOrganizedTourDisplayOrderRef } from "./resolveOrganizedTourDisplayOrderRef";
import { resolveOrganizedTourDisplaySummary } from "./resolveOrganizedTourDisplaySummary";
import { resolveOrganizedTourItineraryFallback } from "./resolveOrganizedTourItineraryFallback";

function countTravelersBreakdown(roomsAndPaxInfo) {
  if (!Array.isArray(roomsAndPaxInfo)) {
    return { adults: 0, children: 0, infants: 0, total: 0, label: "" };
  }
  let adults = 0;
  let children = 0;
  let infants = 0;
  roomsAndPaxInfo.forEach((room) => {
    adults += Number(room?.adults) || 0;
    children += Number(room?.children) || 0;
    infants += Number(room?.infants) || 0;
  });
  const total = adults + children + infants;
  const parts = [];
  if (adults) parts.push(`${adults} מבוגרים`);
  if (children) parts.push(`${children} ילדים`);
  if (infants) parts.push(`${infants} תינוקות`);
  return {
    adults,
    children,
    infants,
    total,
    label: total > 0 ? parts.join(" · ") : "",
  };
}

function stripForSnippet(text, maxLen) {
  const s = String(text ?? "").replace(/\s+/g, " ").trim();
  if (!s) return "";
  if (s.length <= maxLen) return s;
  return `${s.slice(0, maxLen - 1)}…`;
}

/**
 * View-model for organized-tour payment sidebar (UI only).
 *
 * @param {object} params
 * @param {unknown} params.selectedResult
 * @param {unknown} params.roomsAndPaxInfo
 * @param {object|null|undefined} params.ourPrice
 * @param {object|null|undefined} params.orgTourInfo — `useOrganizedTourProfileInfo` shape
 * @param {object|null|undefined} params.savedOrder
 * @param {string|number|null|undefined} params.orderId
 */
export function buildOrganizedTourPaymentViewModel({
  selectedResult,
  roomsAndPaxInfo,
  ourPrice,
  orgTourInfo,
  savedOrder,
  orderId,
}) {
  const orderSummary =
    savedOrder && typeof savedOrder === "object"
      ? resolveOrganizedTourDisplaySummary(savedOrder, { silent: true })
      : null;

  const profileVm = buildOrganizedTourProfileViewModel({
    selectedResult,
    resourceUrl: orgTourInfo?.resourceUrl ?? null,
    ourPrice,
    savedOrder,
  });

  const tourName =
    orderSummary && orderSummary.tourNameSource !== "fallback"
      ? orderSummary.tourName
      : profileVm.tourName && profileVm.tourName !== "טיול מאורגן"
        ? profileVm.tourName
        : orderSummary?.tourName || profileVm.tourName;

  const routeLabelMerged =
    orderSummary?.routeSource !== "empty" && String(orderSummary?.routeLabel || "").trim()
      ? String(orderSummary.routeLabel).trim()
      : String(profileVm.routeLabel || "").trim();

  const pax = countTravelersBreakdown(roomsAndPaxInfo);
  const linkedFlights = selectedResult?.linkedFlights;
  const legs = extractOrganizedTourFlightLegs(linkedFlights);
  const flightLines = legs.map((leg) => {
    const dep = formatOrganizedTourFlightDateTime(leg.depDate);
    const from = leg.depCity || "";
    const to = leg.arrCity || "";
    const line = [from && to ? `${from} → ${to}` : "", dep.date, dep.time]
      .filter(Boolean)
      .join(" · ");
    return {
      segmentLabel: String(leg.segmentLabel || ""),
      line: line || "—",
    };
  });

  const contentText = orgTourInfo?.contentText ?? "";
  const termsSnippet = stripForSnippet(contentText, 320);
  const hasTerms =
    termsSnippet.length > 40 ||
    (orgTourInfo?.resourceUrl && String(orgTourInfo.resourceUrl).trim().length > 0) ||
    String(orgTourInfo?.contentHtmlSanitized || "").trim().length > 80;

  const clientRemarks = selectedResult?.clientRemarks;
  const orderNotesSnippet =
    typeof clientRemarks === "string" && clientRemarks.trim()
      ? stripForSnippet(clientRemarks.replace(/<[^>]+>/g, " "), 240)
      : "";

  const DISCLOSURE_SNIPPET =
    "שינוי או ביטול הזמנה כרוכים בדמי טיפול. תנאי הביטול נקבעים על ידי הספק.";

  const itineraryFallback = resolveOrganizedTourItineraryFallback({
    selectedResult,
    savedOrder,
    orgTourInfo: {
      contentText,
      contentHtmlSanitized: orgTourInfo?.contentHtmlSanitized,
      resourceUrl: orgTourInfo?.resourceUrl,
    },
  });

  const hrRefMeta =
    savedOrder && typeof savedOrder === "object"
      ? resolveOrganizedTourDisplayOrderRef(savedOrder, { log: true })
      : { display: null, source: null, raw: null };

  return {
    ...profileVm,
    tourName,
    routeLabel: routeLabelMerged,
    orderId: orderId != null ? String(orderId) : "",
    hrOrderRefDisplay: hrRefMeta.display,
    hrOrderRefSource: hrRefMeta.source,
    passengerCount: pax,
    flightLines,
    hasFlights: flightLines.length > 0,
    termsSnippet,
    hasTerms,
    orderNotesSnippet,
    cancellationHint: DISCLOSURE_SNIPPET,
    itineraryFallback,
  };
}

function buildOverlayPassengersFromRooms(roomsAndPaxInfo) {
  if (!Array.isArray(roomsAndPaxInfo)) return [];
  const out = [];
  roomsAndPaxInfo.forEach((room) => {
    const adults = Array.isArray(room?.adults) ? room.adults : [];
    adults.forEach((p) => {
      const fn = p?.firstName || p?.FirstName || "";
      const ln = p?.lastName || p?.LastName || "";
      const name = [fn, ln].filter(Boolean).join(" ").trim();
      if (name) out.push({ label: "מבוגר", name });
    });
    const children = Array.isArray(room?.children) ? room.children : [];
    children.forEach((p) => {
      const fn = p?.firstName || p?.FirstName || "";
      const ln = p?.lastName || p?.LastName || "";
      const name = [fn, ln].filter(Boolean).join(" ").trim();
      if (name) out.push({ label: "ילד", name });
    });
    const infants = Array.isArray(room?.infants) ? room.infants : [];
    infants.forEach((p) => {
      const fn = p?.firstName || p?.FirstName || "";
      const ln = p?.lastName || p?.LastName || "";
      const name = [fn, ln].filter(Boolean).join(" ").trim();
      if (name) out.push({ label: "תינוק", name });
    });
  });
  return out;
}

function pickLeadCustomerName(savedOrder, roomsAndPaxInfo) {
  const direct =
    savedOrder?.customerName ||
    savedOrder?.customer_name ||
    savedOrder?.fullname ||
    savedOrder?.full_name ||
    "";
  if (String(direct).trim()) return String(direct).trim();
  if (Array.isArray(roomsAndPaxInfo) && roomsAndPaxInfo[0]) {
    const a = roomsAndPaxInfo[0].adults?.[0] || roomsAndPaxInfo[0]?.Adults?.[0];
    if (a) {
      const fn = a.firstName || a.FirstName || "";
      const ln = a.lastName || a.LastName || "";
      const name = [fn, ln].filter(Boolean).join(" ").trim();
      if (name) return name;
    }
  }
  return "";
}

/**
 * Hero + overlay fields aligned with hotel payment layout (paymentagent.scss).
 * @param {object} params
 * @param {object} params
 * @param {object} params.paymentVm — תוצאת buildOrganizedTourPaymentViewModel
 * @param {object|null|undefined} params.savedOrder
 * @param {unknown} params.roomsAndPaxInfo
 * @param {string} [params.agentFirstName]
 * @param {string} [params.agentNameFromOrder]
 */
export function buildOrganizedTourPaymentHeroLayoutModel({
  paymentVm,
  savedOrder,
  roomsAndPaxInfo,
  agentFirstName = "",
  agentNameFromOrder = "",
}) {
  const vm = paymentVm || {};
  const metaItems = [];
  if (vm.routeLabel) metaItems.push({ key: "route", text: `מסלול: ${vm.routeLabel}` });
  const dateLine =
    vm.dateRangeDisplay ||
    (vm.depDateLabel && vm.retDateLabel ? `${vm.depDateLabel} — ${vm.retDateLabel}` : "");
  if (dateLine) metaItems.push({ key: "dates", text: `תאריכים: ${dateLine}` });
  if (vm.passengerCount?.label) {
    metaItems.push({ key: "pax", text: `נוסעים: ${vm.passengerCount.label}` });
  }
  if (vm.hasFlights && vm.flightLines?.length) {
    const fl = vm.flightLines[0];
    const flText = `${fl.segmentLabel ? `${fl.segmentLabel} · ` : ""}${fl.line}`;
    metaItems.push({ key: "fl", text: `טיסה: ${flText}` });
  }

  return {
    compactTitle: vm.tourName || "טיול מאורגן",
    compactImage: vm.heroImage || "",
    heroMetaItems: metaItems,
    overlayAgentName: String(agentNameFromOrder || agentFirstName || "").trim(),
    overlayClientName: pickLeadCustomerName(savedOrder, roomsAndPaxInfo),
    overlayOrderNumber: String(paymentVm.hrOrderRefDisplay || "").trim(),
    overlayPassengers: buildOverlayPassengersFromRooms(roomsAndPaxInfo),
  };
}

/**
 * Adapter: SSOT row מש־getLdsOrder → אותו VM כמו במסך פרופיל/תשלום מאורגן.
 */
export function buildOrganizedTourPaymentViewModelFromOrder(orderRow, parts) {
  const oid = orderRow?.id ?? orderRow?.ourLdsId ?? parts?.orderId ?? "";
  return buildOrganizedTourPaymentViewModel({
    selectedResult: parts?.selectedResult,
    roomsAndPaxInfo: parts?.roomsAndPaxInfo,
    ourPrice: parts?.ourPrice,
    orgTourInfo: parts?.orgTourInfo,
    savedOrder: orderRow,
    orderId: oid,
  });
}
